import styles from './StageEngagementTitle.module.css';
import { useEffect, useMemo, useState } from 'react';
import { useScreenTheme } from '@/providers/ScreenThemeStateProvider/EventScreenThemeContext';
import { cn } from '@/lib/utils';
import {
  raffleJoinBubbleText,
  raffleStatusFromEngagement,
} from './raffle/stage/raffleChrome';
import { SLIDE_QR_LABEL_FONT_SIZE } from './slides/slideFrame';
import {
  isTriviaEngagement,
  triviaJoinBubbleText,
} from './trivia/triviaChrome';

export const StageEngagementTitle = ({
  slideSize,
}: {
  slideSize?: { width: number; height: number };
} = {}) => {
  const { state } = useScreenTheme();
  const [text, setText] = useState('');
  const raffleStatus = raffleStatusFromEngagement(state.activeEngagement);
  const bubbleText = isTriviaEngagement(state.activeEngagement)
    ? triviaJoinBubbleText(state.activeEngagement?.title)
    : raffleJoinBubbleText({
        status: raffleStatus,
        engagementTitle: state.activeEngagement?.title,
      });
  const drawingSoon = raffleStatus === 'READY_TO_DRAW';

  useEffect(() => {
    if (bubbleText === null) {
      setText('');
      return;
    }
    if (bubbleText.length) {
      setText(bubbleText);
    }
  }, [bubbleText]);

  const inlineStyles = useMemo(() => {
    if (slideSize) {
      return {
        fontSize: SLIDE_QR_LABEL_FONT_SIZE,
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        boxSizing: 'border-box' as const,
        padding: drawingSoon
          ? `${slideSize.height * 0.007}px ${slideSize.width * 0.011}px`
          : `${slideSize.height * 0.008}px ${slideSize.width * 0.013}px`,
      };
    }
    return {
      fontSize: drawingSoon
        ? '1.05vw'
        : `${(text.length || 0) > 10 ? '1.8vw' : '2.1vw'}`,
    };
  }, [drawingSoon, slideSize, text.length]);

  if (!text) {
    return null;
  }

  return (
    <h1
      style={inlineStyles}
      data-name="ACTIVE_ENGAGEMENT_TITLE"
      data-drawing-soon={drawingSoon ? 'true' : 'false'}
      className={cn(
        'whitespace-nowrap text-center font-screen-body transition-opacity duration-1000',
        drawingSoon
          ? styles.drawingSoonTitle
          : `rounded-full bg-white text-[#593312] ${styles.activeEngagementTitle}`,
        state.activeEngagement ? 'opacity-100' : 'opacity-0',
      )}
    >
      {text}
    </h1>
  );
};
